<script setup lang="ts">
  import { computed, onMounted, onUnmounted } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import moment from "moment";
  import { Copy, Download, Pencil, Trash2 } from "lucide-vue-next";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { EXPORTS_PAGE_SIZE, exportTypeLabel, useExportsStore } from "@stores/survey/exports";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { DataTable, DataTableActions } from "@/components/episto-ui/data-table";
  import type { DataTableAction, DataTableColumn } from "@/components/episto-ui/data-table";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import type { SurveyExport } from "@/types";

  const router = useRouter();
  const { openRow } = useRowNavigation();
  const { token: surveyToken } = storeToRefs(useSurveyStore());
  const { currentTargets } = storeToRefs(useSurveyTargetsStore());
  const resultsV2Store = useResultsV2Store();
  const { savedPopulations } = storeToRefs(resultsV2Store);
  const exportsStore = useExportsStore();
  const { exports, exportsCount, currentPage, loading, searchQuery, sortColumn, sortDirection } =
    storeToRefs(exportsStore);

  const {
    isOpen: isDeleteAlertOpen,
    target: exportToDelete,
    requestDelete,
    confirm: confirmDelete,
  } = useConfirmDelete<SurveyExport>((item) => exportsStore.deleteExport(item.token));
  const totalPages = computed(() => Math.ceil(exportsCount.value / EXPORTS_PAGE_SIZE) || 1);

  onMounted(async () => {
    await exportsStore.fetchExports(1);
    if (!savedPopulations.value.length) resultsV2Store.fetchSavedPopulations().catch(() => {});
  });
  onUnmounted(() => exportsStore.resetFilters());

  const columns: DataTableColumn<SurveyExport>[] = [
    { key: "name", label: "Name", sortable: true, class: "font-medium" },
    { key: "type", label: "Type", sortable: true },
    { key: "filter", label: "Filter" },
    { key: "created_at", label: "Created", sortable: true },
  ];

  type NamedToken = { token: string; name: string };
  const targets = computed(() => currentTargets.value as NamedToken[]);
  const populations = computed(() => savedPopulations.value as NamedToken[]);

  const filterLabel = (item: SurveyExport) => {
    const parts: string[] = [];
    const target = targets.value.find((t) => t.token === item.target_token);
    if (target) parts.push(target.name);
    const population = populations.value.find((p) => p.token === item.population);
    if (population) parts.push(population.name);
    return parts.length ? parts.join(" · ") : "All respondents";
  };

  const rowActions: DataTableAction[] = [
    { key: "duplicate", label: "Duplicate", icon: Copy },
    { key: "delete", label: "Delete", icon: Trash2, variant: "destructive", separatorBefore: true },
  ];

  const openNew = () => router.push({ name: "SurveyExportNew", params: { token: surveyToken.value } });
  const openEdit = (item: SurveyExport, event?: MouseEvent | KeyboardEvent) =>
    openRow({ name: "SurveyExportEdit", params: { token: surveyToken.value, exportToken: item.token } }, event);

  // "Download" regenerates with the row's parameters; the toaster triggers the actual download.
  const downloadExport = (item: SurveyExport) => exportsStore.generateExport(item.token);

  async function handleAction(key: string, item: SurveyExport) {
    if (key === "duplicate") await exportsStore.duplicateExport(item.token);
    if (key === "delete") requestDelete(item);
  }
</script>

<template>
  <IndexPageLayout
    title="Exports"
    data-cy="survey-exports-page"
    action-label="New export"
    @action="openNew">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search exports by name..." />
    </template>

    <DataTable
      :items="exports"
      :columns="columns"
      :loading="loading"
      :current-page="currentPage"
      :total-pages="totalPages"
      :items-per-page="EXPORTS_PAGE_SIZE"
      :sort-column="sortColumn"
      :sort-direction="sortDirection"
      item-key="token"
      empty-message="No exports yet. Create your first export to find it here."
      table-class="[&_tbody_tr]:cursor-pointer"
      @sort="exportsStore.sortBy"
      @page-change="exportsStore.fetchExports"
      @row-click="openEdit">
      <template #cell-type="{ item }">
        <Badge variant="secondary">{{ exportTypeLabel(item.file_format) }}</Badge>
      </template>
      <template #cell-filter="{ item }">
        <span class="text-muted-foreground">{{ filterLabel(item) }}</span>
      </template>
      <template #cell-created_at="{ item }">
        {{ moment(item.created_at).format("MMM D, YYYY") }}
      </template>
      <template #actions="{ item }">
        <div
          class="flex items-center"
          @click.stop>
          <Button
            variant="ghost"
            class="h-8 w-8 p-0"
            title="Edit configuration"
            @click="openEdit(item, $event)">
            <Pencil class="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            class="h-8 w-8 p-0"
            title="Download an up-to-date version"
            @click="downloadExport(item)">
            <Download class="h-4 w-4" />
          </Button>
          <DataTableActions
            :actions="rowActions"
            @action="handleAction($event, item)" />
        </div>
      </template>
    </DataTable>

    <template #dialogs>
      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Delete export?"
        confirm-label="Delete"
        @confirm="confirmDelete">
        This will remove
        <span class="font-bold">{{ exportToDelete?.name }}</span>
        from the history. This action cannot be undone.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
